import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Chip,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  Button,
  Tooltip,
  useTheme,
  TablePagination,
  TableSortLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SectionCard, SectionHeader, SectionLoading, NestedPanel, SentimentBadge } from '../components/ui/Section';
import { useOutletContext, useParams } from 'react-router-dom';
import { useFilterContext } from '../context/FilterContext';
import { format } from 'date-fns';
import { useAxiosClient } from '../utils/useAxiosClient';
import { formatTikTokLink } from '../utils/linkFormatting';
import { Bubble } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  LinearScale, 
  PointElement, 
  Tooltip as ChartTooltip, 
  Legend 
} from 'chart.js';

// Register the required chart.js components
ChartJS.register(LinearScale, PointElement, ChartTooltip, Legend);

// Import the components from EmployeeBrandPersonalMetrics page or create simplified versions
interface KeywordItem {
  keyword: string;
  brand: string;
  frequency: number;
  relevance: number;
  sentiment: number;
}

interface SyntaxCardData {
  keywords: KeywordItem[];
}

interface MessagePreviewItem {
  brand: string;
  message: string;
  post_link: string;
  type: 'Post' | 'Comment';
  likes: number;
  post_datetime?: string;
  platform: string;
}

interface FilterProps {
  selectedBrands: string[];
  selectedPlatforms: string[];
  selectedPillars: string[];
  startDate: string | null;
  endDate: string | null;
}

// Simple loading component
const Loading = () => <SectionLoading height={240} />;

// Keywords Syntax Card component
const KeywordSyntaxCard = ({ 
  selectedBrands, 
  selectedPillars, 
  startDate, 
  endDate,
  onKeywordClick,
  selectedKeyword,
  selectedPlatforms,
  searchKeyword
}: { 
  selectedBrands: string[], 
  selectedPillars: string[], 
  startDate: string | null, 
  endDate: string | null,
  onKeywordClick: (keyword: string) => void,
  selectedKeyword: string | null,
  selectedPlatforms: string[],
  searchKeyword?: string
}) => {
  const [syntaxData, setSyntaxData] = useState<SyntaxCardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // -------------------- Keyword table sorting state --------------------
  type KeywordSortField = 'keyword' | 'brand' | 'frequency' | 'relevance' | 'sentiment';
  const [keywordSortField, setKeywordSortField] = useState<KeywordSortField>('frequency');
  const [keywordSortDirection, setKeywordSortDirection] = useState<'asc' | 'desc'>('desc');
  const axiosClient = useAxiosClient();
  const theme = useTheme();
  
  // Get brand colors from FilterContext
  const { brandColors, brandColorsLoading } = useFilterContext();
  
  // Add ref to track the last request parameters
  const lastRequestRef = React.useRef<string>('');

  // Fetch actual data from API
  useEffect(() => {
    const fetchSyntaxData = async () => {
      // Construct query parameters
      const params = new URLSearchParams();
      
      // Append all selected brands (support multiple)
      selectedBrands.forEach(brand => {
        params.append('brands', brand);
      });
      
      selectedPillars.forEach(pillar => {
        params.append('pillars', pillar);
      });
      
      selectedPlatforms.forEach(platform => {
        params.append('platforms', platform);
      });
      
      if (startDate) {
        params.append('start_date', startDate);
      }
      if (endDate) {
        params.append('end_date', endDate);
      }
      
      // Add keyword parameter if provided
      if (searchKeyword && searchKeyword.trim()) {
        params.append('keyword', searchKeyword.trim());
      }
      
      // Add the sort_mode parameter specifically for Keyword Analysis
      params.append('sort_mode', 'frequency');
      
      const paramString = params.toString() ? `?${params.toString()}` : '';
      
      // Compare with the last request to avoid duplicate calls
      const currentRequest = `/api/personal-syntax-card${paramString}`;
      if (currentRequest === lastRequestRef.current) {
        console.log('Skipping duplicate syntax card request');
        return;
      }
      
      // Update the last request reference
      lastRequestRef.current = currentRequest;
      
      setLoading(true);
      try {
        console.log(`Making request to ${currentRequest}`);
        const response = await axiosClient.get(currentRequest);
        
        if (response.data && response.data.keywords && response.data.keywords.length > 0) {
          setSyntaxData(response.data);
          setError(null);
        } else {
          // If no data, fall back to placeholder
          setError('No keyword data found for the selected filters');
          setSyntaxData(placeholderSyntaxData);
        }
      } catch (error) {
        console.error('Error fetching syntax data:', error);
        setError('Error loading keyword data');
        // Fall back to placeholder data
        setSyntaxData(placeholderSyntaxData);
      } finally {
        setLoading(false);
      }
    };

    fetchSyntaxData();
  }, [axiosClient, selectedBrands, selectedPillars, selectedPlatforms, startDate, endDate, searchKeyword]);

  if (loading) {
    return <Loading />;
  }

  // Show loading state while brand colors are loading
  if (brandColorsLoading) {
    return <SectionLoading label="Loading brand colors..." height={240} />;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>{error}</Typography>
    );
  }

  const data = syntaxData?.keywords || [];

  // -------------------- Keyword table sorting --------------------
  const sortedKeywordData = [...data].sort((a, b) => {
    let comparison = 0;
    switch (keywordSortField) {
      case 'keyword':
      case 'brand':
        comparison = a[keywordSortField].localeCompare(b[keywordSortField]);
        break;
      case 'frequency':
      case 'relevance':
      case 'sentiment':
        // @ts-ignore – keys are numeric here
        comparison = a[keywordSortField] - b[keywordSortField];
        break;
      default:
        comparison = 0;
    }
    return keywordSortDirection === 'asc' ? comparison : -comparison;
  });

  // Handle sort changes for keyword table
  const handleKeywordSort = (field: KeywordSortField) => {
    const isAsc = keywordSortField === field && keywordSortDirection === 'asc';
    setKeywordSortDirection(isAsc ? 'desc' : 'asc');
    setKeywordSortField(field);
  };

  // Function to get brand color with fallback
  const getBrandColor = (brand: string): string => {
    return brandColors[brand] || `hsl(${brand.length * 45}, 70%, 50%)`;
  };

  // Helper function to convert hex to RGB
  const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Handle 3-digit hex
    if (hex.length === 3) {
      hex = hex.split('').map(char => char + char).join('');
    }
    
    const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Calculate min and max frequency for relative bubble scaling
  const frequencies = data.map(item => item.frequency);
  const minFrequency = Math.min(...frequencies);
  const maxFrequency = Math.max(...frequencies);
  const frequencyRange = maxFrequency - minFrequency;
  
  // Function to calculate relative bubble size
  const calculateBubbleSize = (frequency: number): number => {
    // If all frequencies are the same, use a default size
    if (frequencyRange === 0) return 8;
    
    // Scale frequency to 0-1 range
    const normalizedFreq = (frequency - minFrequency) / frequencyRange;
    
    // Map to bubble radius range (min: 4, max: 20)
    const minRadius = 4;
    const maxRadius = 20;
    return minRadius + (normalizedFreq * (maxRadius - minRadius));
  };

  // Prepare data for bubble chart - SWAP AXES: sentiment on X, relevance on Y
  const bubbleData = {
    datasets: [{
      label: 'Keywords',
      data: data.slice(0, 30).map(item => ({
        x: item.sentiment,          // Swapped: sentiment on X-axis
        y: item.relevance,          // Swapped: relevance on Y-axis
        r: calculateBubbleSize(item.frequency), // Use relative scaling based on dataset min/max
        keyword: item.keyword,
        brand: item.brand,
        frequency: item.frequency
      })),
      backgroundColor: data.slice(0, 30).map(item => {
        // Get base color for the brand
        const baseColor = getBrandColor(item.brand);
        // Extract RGB components
        const rgb = hexToRgb(baseColor);
        if (!rgb) return baseColor;
        
        // Calculate opacity based on frequency (higher frequency = darker)
        const maxFreq = Math.max(...data.map(d => d.frequency));
        const opacity = 0.3 + ((item.frequency / maxFreq) * 0.7); // Scale between 0.3 and 1.0
        
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
      }),
      borderColor: data.slice(0, 30).map(item => {
        const baseColor = getBrandColor(item.brand);
        const rgb = hexToRgb(baseColor);
        if (!rgb) return baseColor;
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.8)`;
      }),
      borderWidth: 1
    }]
  };

  const bubbleOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Sentiment',        // Swapped: X-axis is now Sentiment
          color: theme.palette.text.secondary
        },
        ticks: {
          color: theme.palette.text.secondary
        },
        grid: { color: theme.palette.divider },
        border: { color: theme.palette.divider }
      },
      y: {
        title: {
          display: true,
          text: 'Relevance',        // Swapped: Y-axis is now Relevance
          color: theme.palette.text.secondary
        },
        min: 0,
        max: Math.max(...data.map(item => item.relevance)) * 1.1,
        ticks: {
          color: theme.palette.text.secondary
        },
        grid: { color: theme.palette.divider },
        border: { color: theme.palette.divider }
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const { keyword, brand, frequency } = context.raw;
            return [
              `Keyword: ${keyword}`,
              `Brand: ${brand}`,
              `Relevance: ${context.raw.y.toFixed(2)}`, // Updated for swapped axes
              `Sentiment: ${context.raw.x.toFixed(2)}`, // Updated for swapped axes
              `Frequency: ${frequency}`
            ];
          }
        }
      },
      legend: {
        display: false
      }
    },
    onClick: (_: any, elements: any[]) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const keyword = data[index].keyword;
        onKeywordClick(keyword);
      }
    },
    layout: {
      padding: 20
    }
  };

  return (
    <>
      <SectionHeader
        title="Top 100 Keywords by Relevance"
        info="Interactive keyword analysis showing top keywords by frequency and relevance. Click keywords in the table or bubble chart to filter the message preview below. Sentiment scores are color-coded: green (positive), gray (neutral), red (negative)."
        description="Click a keyword in the chart or the table to filter the message preview below."
      />

      <Grid container spacing={2}>
        {/* Bubble Chart */}
        <Grid item xs={12} md={6}>
          <NestedPanel sx={{ height: 440, display: 'flex', flexDirection: 'column' }}>
            <SectionHeader title="Sentiment vs. relevance" level="sub" sx={{ mb: 0.5 }} />
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
              <Bubble data={bubbleData} options={bubbleOptions} />
            </Box>
            <Typography variant="caption" sx={{ mt: 0.5 }}>
              Bubble size shows how often a keyword appears (top 30 shown).
            </Typography>
          </NestedPanel>
        </Grid>

        {/* Keywords Table */}
        <Grid item xs={12} md={6}>
          <TableContainer sx={(t) => ({ maxHeight: 440, border: `1px solid ${t.palette.divider}`, borderRadius: '10px' })}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: 'background.paper' } }}>
                  <TableCell>
                    <TableSortLabel
                      active={keywordSortField === 'keyword'}
                      direction={keywordSortField === 'keyword' ? keywordSortDirection : 'asc'}
                      onClick={() => handleKeywordSort('keyword')}
                    >
                      Keyword
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={keywordSortField === 'brand'}
                      direction={keywordSortField === 'brand' ? keywordSortDirection : 'asc'}
                      onClick={() => handleKeywordSort('brand')}
                    >
                      Brand
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ width: '80px' }}>
                    <TableSortLabel
                      active={keywordSortField === 'frequency'}
                      direction={keywordSortField === 'frequency' ? keywordSortDirection : 'asc'}
                      onClick={() => handleKeywordSort('frequency')}
                    >
                      Frequency
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ width: '80px' }}>
                    <TableSortLabel
                      active={keywordSortField === 'relevance'}
                      direction={keywordSortField === 'relevance' ? keywordSortDirection : 'asc'}
                      onClick={() => handleKeywordSort('relevance')}
                    >
                      Relevance
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ width: '80px' }}>
                    <TableSortLabel
                      active={keywordSortField === 'sentiment'}
                      direction={keywordSortField === 'sentiment' ? keywordSortDirection : 'asc'}
                      onClick={() => handleKeywordSort('sentiment')}
                    >
                      Sentiment
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedKeywordData.map((item, index) => (
                  <TableRow
                    key={index}
                    hover
                    selected={selectedKeyword === item.keyword}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => onKeywordClick(item.keyword)}
                  >
                    <TableCell sx={{ fontWeight: selectedKeyword === item.keyword ? 600 : 400, maxWidth: '120px' }}>
                      <Tooltip title={item.keyword}>
                        <Typography noWrap variant="body2" sx={{ fontWeight: 'inherit' }}>
                          {item.keyword.length > 16 ? `${item.keyword.substring(0, 16)}...` : item.keyword}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: getBrandColor(item.brand), flexShrink: 0 }} />
                        {item.brand}
                      </Box>
                    </TableCell>
                    <TableCell align="center">{item.frequency}</TableCell>
                    <TableCell align="center">{item.relevance.toFixed(2)}</TableCell>
                    <TableCell align="center">
                      <SentimentBadge value={item.sentiment} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </>
  );
};

// Message Preview component
const MessagePreview = ({
  selectedKeyword,
  selectedBrands,
  selectedPillars,
  startDate,
  endDate,
  selectedPlatforms,
}: {
  selectedKeyword: string | null;
  selectedBrands: string[];
  selectedPillars: string[];
  startDate: string | null;
  endDate: string | null;
  selectedPlatforms: string[];
}) => {
  const [messages, setMessages] = useState<MessagePreviewItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // -------------------- Message preview sorting state --------------------
  type PreviewSortField = 'brand' | 'message' | 'platform' | 'type' | 'likes' | 'date';
  const [previewSortField, setPreviewSortField] = useState<PreviewSortField>('date');
  const [previewSortDirection, setPreviewSortDirection] = useState<'asc' | 'desc'>('desc');
  const axiosClient = useAxiosClient();
  
  // Use boolean state for toggles again, like EmployeeBrandPersonalMetrics
  const [showPosts, setShowPosts] = useState(true);
  const [showComments, setShowComments] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Add ref to track the last request parameters
  const lastRequestRef = React.useRef<string>('');

  useEffect(() => {
    const fetchMessages = async () => {
      // Construct query parameters
      const params = new URLSearchParams();
      
      // Append all selected brands (support multiple)
      selectedBrands.forEach(brand => {
        params.append('brands', brand);
      });

      if (selectedKeyword) {
        params.append('keyword', selectedKeyword);
      }

      selectedPillars.forEach(pillar => {
        params.append('pillars', pillar);
      });
      
      selectedPlatforms.forEach(platform => {
        params.append('platforms', platform);
      });

      if (startDate) {
        params.append('start_date', startDate);
      }
      if (endDate) {
        params.append('end_date', endDate);
      }

      const paramString = params.toString() ? `?${params.toString()}` : '';
      
      // Compare with the last request to avoid duplicate calls
      const currentRequest = `/api/message-preview${paramString}`;
      if (currentRequest === lastRequestRef.current) {
        console.log('Skipping duplicate message preview request');
        return;
      }
      
      // Update the last request reference
      lastRequestRef.current = currentRequest;

      setLoading(true);
      try {
        console.log(`Making request to ${currentRequest}`);
        const response = await axiosClient.get(currentRequest);
        
        if (response.data && response.data.messages) {
          setMessages(response.data.messages);
          setError(null);
        } else {
          setError('No messages found for the current filters');
          setMessages([]);
        }
      } catch (error) {
        console.error('Error fetching messages:', error);
        setError('Error loading message previews');
        setMessages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [axiosClient, selectedKeyword, selectedBrands, selectedPillars, selectedPlatforms, startDate, endDate]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [showPosts, showComments, selectedKeyword, selectedBrands, selectedPillars, selectedPlatforms, startDate, endDate]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 4, textAlign: 'center' }}>{error}</Typography>
    );
  }

  // Filter messages based on the boolean toggle state
  const filteredMessages = messages.filter(message => {
    if (message.type === 'Post' && !showPosts) return false;
    if (message.type === 'Comment' && !showComments) return false;
    return true;
  });

  // -------------------- Message preview table sorting --------------------
  // Handle sort changes for message preview table
  const handlePreviewSort = (field: PreviewSortField) => {
    const isAsc = previewSortField === field && previewSortDirection === 'asc';
    setPreviewSortDirection(isAsc ? 'desc' : 'asc');
    setPreviewSortField(field);
    setPage(0);
  };

  const sortedMessages = [...filteredMessages].sort((a, b) => {
    let comparison = 0;
    switch (previewSortField) {
      case 'likes':
        comparison = (a.likes || 0) - (b.likes || 0);
        break;
      case 'date':
        const dateA = a.post_datetime ? new Date(a.post_datetime).getTime() : 0;
        const dateB = b.post_datetime ? new Date(b.post_datetime).getTime() : 0;
        comparison = dateA - dateB;
        break;
      case 'brand':
        comparison = a.brand.localeCompare(b.brand);
        break;
      case 'message':
        comparison = a.message.localeCompare(b.message);
        break;
      case 'platform':
        comparison = a.platform.localeCompare(b.platform);
        break;
      case 'type':
        comparison = a.type.localeCompare(b.type);
        break;
      default:
        comparison = 0;
    }
    return previewSortDirection === 'asc' ? comparison : -comparison;
  });

  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Button toggle handler
  const handleToggleFilter = (type: 'Post' | 'Comment') => {
    if (type === 'Post') {
      setShowPosts(!showPosts);
    } else {
      setShowComments(!showComments);
    }
    // Ensure at least one is always shown (optional)
    // if (type === 'Post' && !showComments && showPosts) return; // Prevent hiding the last one
    // if (type === 'Comment' && !showPosts && showComments) return; // Prevent hiding the last one
  };

  // Pagination logic
  const paginatedMessages = sortedMessages.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (filteredMessages.length === 0 && !loading) {
    return (
      <Box>
        {/* Show toggle buttons even when no results match the filter */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <ToggleButtonGroup
          size="small"
          aria-label="message types"
          value={[showPosts ? 'Post' : null, showComments ? 'Comment' : null].filter(Boolean) as string[]}
          onChange={(_e, next: string[]) => {
            if (next.includes('Post') !== showPosts) handleToggleFilter('Post');
            if (next.includes('Comment') !== showComments) handleToggleFilter('Comment');
          }}
        >
          <ToggleButton value="Post">Posts</ToggleButton>
          <ToggleButton value="Comment">Comments</ToggleButton>
        </ToggleButtonGroup>
        </Box>
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          {messages.length > 0
            ? 'Nothing matches the current Posts/Comments selection. Turn one of them back on.'
            : 'No messages for these filters.'
          }
        </Typography>
      </Box>
    );
  }

  const formatPlatform = (platform?: string): string => {
    if (!platform || platform === 'Unknown') return '-';
    
    // Map of platform names to their display format
    const platformMap: Record<string, string> = {
      'facebook': 'Facebook',
      'instagram': 'Instagram',
      'linkedin': 'LinkedIn',
      'twitter': 'Twitter',
      'x': 'X',
      'tiktok': 'TikTok',
      'youtube': 'YouTube'
    };
    
    // Use the mapping or just capitalize the first letter
    const platformLower = platform.toLowerCase();
    return platformMap[platformLower] || 
      (platform.charAt(0).toUpperCase() + platform.slice(1).toLowerCase());
  };



  return (
    <Box>
      {/* Filter controls - Replaced ToggleButtonGroup with styled Buttons */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <ToggleButtonGroup
          size="small"
          aria-label="message types"
          value={[showPosts ? 'Post' : null, showComments ? 'Comment' : null].filter(Boolean) as string[]}
          onChange={(_e, next: string[]) => {
            if (next.includes('Post') !== showPosts) handleToggleFilter('Post');
            if (next.includes('Comment') !== showComments) handleToggleFilter('Comment');
          }}
        >
          <ToggleButton value="Post">Posts</ToggleButton>
          <ToggleButton value="Comment">Comments</ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
          {filteredMessages.length.toLocaleString()} {filteredMessages.length === 1 ? 'result' : 'results'}
        </Typography>
      </Box>

      <TableContainer sx={{ mx: { xs: -2, md: -3 }, width: 'auto', px: { xs: 1, md: 1.5 } }}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { px: 1.5, py: 1 } }}>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={previewSortField === 'brand'}
                  direction={previewSortField === 'brand' ? previewSortDirection : 'asc'}
                  onClick={() => handlePreviewSort('brand')}
                >
                  Brand
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={previewSortField === 'message'}
                  direction={previewSortField === 'message' ? previewSortDirection : 'asc'}
                  onClick={() => handlePreviewSort('message')}
                >
                  Message
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={previewSortField === 'platform'}
                  direction={previewSortField === 'platform' ? previewSortDirection : 'asc'}
                  onClick={() => handlePreviewSort('platform')}
                >
                  Platform
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={previewSortField === 'type'}
                  direction={previewSortField === 'type' ? previewSortDirection : 'asc'}
                  onClick={() => handlePreviewSort('type')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={previewSortField === 'likes'}
                  direction={previewSortField === 'likes' ? previewSortDirection : 'asc'}
                  onClick={() => handlePreviewSort('likes')}
                >
                  Likes
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={previewSortField === 'date'}
                  direction={previewSortField === 'date' ? previewSortDirection : 'asc'}
                  onClick={() => handlePreviewSort('date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Link</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedMessages.map((message, index) => {
              // Find keyword in message if selected
              let messageContent = message.message;
              let keywordIndex = -1;
              if (selectedKeyword) {
                keywordIndex = message.message.toLowerCase().indexOf(selectedKeyword.toLowerCase());
              }
              
              return (
                <TableRow key={index} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{message.brand}</TableCell>
                  <TableCell sx={{ maxWidth: 0, width: '45%' }}>
                    <Tooltip title={message.message} placement="top-start">
                      <Typography noWrap variant="body2">
                        {keywordIndex >= 0 ? (
                          <>
                            {/* Keep the matched keyword in view by trimming long lead-ins */}
                            {keywordIndex > 40 ? '...' + message.message.substring(keywordIndex - 30, keywordIndex) : message.message.substring(0, keywordIndex)}
                            <Box
                              component="mark"
                              sx={(t) => ({ bgcolor: t.ink.plumSoft, color: 'primary.main', fontWeight: 600, px: 0.25, borderRadius: '3px' })}
                            >
                              {message.message.substring(keywordIndex, keywordIndex + selectedKeyword!.length)}
                            </Box>
                            {message.message.substring(keywordIndex + selectedKeyword!.length)}
                          </>
                        ) : (
                          message.message
                        )}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{formatPlatform(message.platform)}</TableCell>
                  <TableCell>{message.type}</TableCell>
                  <TableCell align="center" sx={{ fontVariantNumeric: 'tabular-nums' }}>{(message.likes || 0).toLocaleString()}</TableCell>
                  <TableCell align="center" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {message.post_datetime ? format(new Date(message.post_datetime), 'MM/dd/yy') : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <Link
                      href={formatTikTokLink(message.post_link, message.platform)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}
                    >
                      View <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Pagination controls */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <TablePagination
          component="div"
          count={filteredMessages.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Box>
    </Box>
  );
};

// Placeholder data for the syntax card
const placeholderSyntaxData: SyntaxCardData = {
  keywords: [
    { keyword: 'keyword1', brand: 'Brand A', frequency: 10, relevance: 0.85, sentiment: 0.7 },
    { keyword: 'keyword2', brand: 'Brand B', frequency: 8, relevance: 0.75, sentiment: 0.5 },
    { keyword: 'keyword3', brand: 'Brand C', frequency: 6, relevance: 0.65, sentiment: 0.3 },
    { keyword: 'keyword4', brand: 'Brand A', frequency: 5, relevance: 0.60, sentiment: 0.1 },
    { keyword: 'keyword5', brand: 'Brand B', frequency: 4, relevance: 0.55, sentiment: -0.2 },
  ]
};

// Main KeywordAnalysis component
const KeywordAnalysis: React.FC = () => {
  // Get filters from outlet context (provided by Layout) for backward compatibility
  const filters = useOutletContext<FilterProps>();
  const { orgId: orgIdStr } = useParams<{ orgId: string }>();
  const orgId = parseInt(orgIdStr || '0', 10);
  
  // Get filters from FilterContext directly
  const { 
    selectedBrands, 
    selectedPlatforms, 
    selectedPillars, 
    startDate, 
    endDate 
  } = useFilterContext();
  
  // Format dates for API calls
  const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : filters.startDate;
  const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : filters.endDate;

  // State for keyword search and language filter
  const [keywordSearch, setKeywordSearch] = useState<string>('');
  const [language, setLanguage] = useState<string>('all');
  const [selectedKeyword, setSelectedKeyword] = useState<string | null>(null);

  // Add state for active search term (what's actually being searched)
  const [activeSearchKeyword, setActiveSearchKeyword] = useState<string>('');

  // Define available languages
  const languages = [
    { value: 'all', label: 'All Languages' },
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'fr', label: 'French' },
  ];

  // Handle search submission
  const handleSearchSubmit = () => {
    setActiveSearchKeyword(keywordSearch.trim());
  };

  // Handle clear search
  const handleClearSearch = () => {
    setKeywordSearch('');
    setActiveSearchKeyword('');
    setSelectedKeyword(null);
  };

  // Check if search button should be enabled
  const isSearchEnabled = keywordSearch.trim().length > 0;
  const hasActiveSearch = activeSearchKeyword.length > 0;

  const activeFilter = selectedKeyword || (hasActiveSearch ? activeSearchKeyword : null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: '80ch', mt: -1 }}>
        Explore the keywords in your content: find patterns, see which brands own a topic, and check the sentiment around it.
      </Typography>

      {/* Keyword search */}
      <SectionCard sx={{ py: { xs: 2, md: 2.5 } }}>
        <Box component="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); if (isSearchEnabled) handleSearchSubmit(); }} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
          <TextField
            value={keywordSearch}
            onChange={(e) => setKeywordSearch(e.target.value)}
            size="small"
            variant="outlined"
            placeholder="Search keywords"
            inputProps={{ 'aria-label': 'Search keywords' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: '1 1 320px', maxWidth: 520 }}
          />
          {/* Hide Language dropdown until multi-language support is ready */}
          <FormControl variant="outlined" size="small" sx={{ display: 'none' }}>
            <InputLabel id="language-select-label">Language</InputLabel>
            <Select
              labelId="language-select-label"
              id="language-select"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              label="Language"
            >
              {languages.map((lang) => (
                <MenuItem key={lang.value} value={lang.value}>
                  {lang.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button type="submit" variant="contained" disabled={!isSearchEnabled} sx={{ height: 40 }}>
            Search
          </Button>
          <Button
            variant="text"
            onClick={handleClearSearch}
            disabled={!hasActiveSearch && !keywordSearch.trim()}
            sx={{ height: 40 }}
          >
            Clear
          </Button>
          {hasActiveSearch && (
            <Chip
              label={`Showing results for "${activeSearchKeyword}"`}
              onDelete={handleClearSearch}
              color="primary"
              variant="outlined"
              sx={{ ml: { md: 'auto' } }}
            />
          )}
        </Box>
      </SectionCard>

      {/* Keyword Syntax Card */}
      <SectionCard>
        <KeywordSyntaxCard
          selectedBrands={selectedBrands}
          selectedPillars={selectedPillars}
          selectedPlatforms={selectedPlatforms}
          startDate={formattedStartDate}
          endDate={formattedEndDate}
          onKeywordClick={(keyword) => setSelectedKeyword(keyword === selectedKeyword ? null : keyword)}
          selectedKeyword={selectedKeyword}
          searchKeyword={activeSearchKeyword}
        />
      </SectionCard>

      {/* Message Preview Card */}
      <SectionCard>
        <SectionHeader
          title={activeFilter ? `Messages mentioning "${activeFilter}"` : 'Message Preview'}
          info="Preview of actual posts and comments containing selected keywords. Click any keyword above to filter this table. Use post/comment toggles to refine content type. Links direct to original social media posts."
          description={activeFilter ? undefined : 'All posts and comments from the selected dates.'}
          actions={
            activeFilter ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setSelectedKeyword(null);
                  if (hasActiveSearch) {
                    handleClearSearch();
                  }
                }}
              >
                Clear keyword
              </Button>
            ) : undefined
          }
        />

        <MessagePreview
          selectedKeyword={activeFilter}
          selectedBrands={selectedBrands}
          selectedPillars={selectedPillars}
          startDate={formattedStartDate}
          endDate={formattedEndDate}
          selectedPlatforms={selectedPlatforms}
        />
      </SectionCard>
    </Box>
  );
};

export default KeywordAnalysis; 